import { DrawerProps } from '@mui/material/Drawer';
import { Breakpoint } from '@mui/material/styles';
import { PropsWithChildren, ReactNode, useCallback, useRef } from 'react';
import { Button } from '@/wrappers/Button';
import { DrawerActions } from './Actions';
import { DrawerContainer, ICallableRef } from './Container';
import { DrawerContent } from './Content';
import { Subtitle } from './Subtitle';
import { DrawerTitle } from './Title';

export type IDrawerProps = Omit<DrawerProps, 'open' | 'onClose'> & {
    name: string;
    open?: boolean;
    title?: ReactNode;
    subtitle?: ReactNode;
    size?: Breakpoint;
    openButtonValue?: ReactNode;
    actions?: (onClose: () => void) => ReactNode;
    onClose?: () => void;
};

const Drawer = ({
    actions,
    children,
    name,
    open,
    openButtonValue,
    subtitle,
    title,
    onClose,
    ...rest
}: PropsWithChildren<IDrawerProps>) => {
    const containerRef = useRef<ICallableRef>(null);
    const wrappedActions = actions ?? (() => null);
    const handleClose = useCallback(() => {
        containerRef?.current?.setOpen(false);
    }, []);
    const handleOpen = useCallback(() => {
        containerRef?.current?.setOpen(true);
    }, []);

    return (
        <>
            <Button name={name ?? ''} onClick={handleOpen} variant="contained">
                {openButtonValue || 'Open'}
            </Button>
            <DrawerContainer {...rest} open={open ?? false} onClose={handleClose}>
                {title && <DrawerTitle>{title}</DrawerTitle>}
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
                <DrawerContent>{children}</DrawerContent>
                <DrawerActions>{wrappedActions(handleClose)}</DrawerActions>
            </DrawerContainer>
        </>
    );
};

const Wrapper = Object.assign(Drawer, {
    Actions: DrawerActions,
    Container: DrawerContainer,
    Content: DrawerContent,
    Subtitle: Subtitle,
    Title: DrawerTitle
});

export { Wrapper as Drawer };
